import { useState, useEffect, useRef, useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from '@tanstack/react-router'
// import { useQueryClient } from '@tanstack/react-query'
import { mdiClose, mdiCheckCircle, mdiAlertCircle, mdiProgressClock, mdiFileImage, mdiFilePdfBox, mdiFileVideo, mdiFileMusic, mdiFileExcel, mdiFileWord, mdiFilePowerpoint, mdiFileCode, mdiArchive, mdiFile, mdiUpload, mdiFolderCog, mdiFolderMove, mdiFolderPlusOutline } from '@mdi/js'
import { EmptyState } from './ui/empty-state'
import { SECTION_ICONS } from '../lib/section-icons'
import { AppIcon } from './ui/icon'
import { Button } from './ui/button'
import { useDocuments, useUploadDocument, useAssetDocumentFolders, useAsset } from '../hooks/useQueries'
import { useLiveSubscription } from '../contexts/LiveEventsContext'
import { DocumentFolderTree } from './documents/DocumentFolderTree'
import { DocumentMoveFolderDialog } from './documents/DocumentMoveFolderDialog'
import { AssetFolderLayoutDialog } from './documents/AssetFolderLayoutDialog'
import { getLocalizedName } from '../lib/localization'
import { collectDroppedFiles, filesFromFileList, appendOriginalPath } from '../lib/dropped-files'
import { ImportSelect } from './imports/ImportSelect'
import { apiBaseUrl } from '../api/client'
import type { components } from '../api/schema'
import { Can } from '../contexts/PermissionsContext'

type Document = components['schemas']['Document']

// The asset documents list is fetched with `include=document_type_uid`, which
// is what the folder grouping keys on (spec 2026-07-27-06, decision 3).
type DocumentWithType = Document & { uid: string; document_type_uid?: string | null }

// Upload file interface
interface UploadFile {
    file: File
    id: string
    status: 'pending' | 'uploading' | 'success' | 'failed'
    progress: number
    retryCount: number
    error?: string
    abortController?: AbortController
    /** Relative path inside a dropped folder — provenance, spec 2026-08-16-04 §3. */
    originalPath?: string
}

// Custom component to handle authenticated thumbnail loading
function DocumentIcon({ document, org }: { document: Document; org: string }) {
    const { file_mime_type, uid, name } = document
    const [thumbFailed, setThumbFailed] = useState(false)

    const isImageOrPdf = (mimeType: string | undefined) => {
        if (!mimeType) return false
        return mimeType.startsWith('image/') || mimeType === 'application/pdf'
    }

    const getFileTypeIcon = (mimeType: string | undefined) => {
        if (!mimeType) return mdiFile

        // Images
        if (mimeType.startsWith('image/')) return mdiFileImage

        // PDFs
        if (mimeType === 'application/pdf') return mdiFilePdfBox

        // Videos
        if (mimeType.startsWith('video/')) return mdiFileVideo

        // Audio
        if (mimeType.startsWith('audio/')) return mdiFileMusic

        // Microsoft Office
        if (mimeType.includes('excel') || mimeType.includes('spreadsheet')) return mdiFileExcel
        if (mimeType.includes('word') || mimeType.includes('document')) return mdiFileWord
        if (mimeType.includes('presentation') || mimeType.includes('powerpoint')) return mdiFilePowerpoint

        // Code files
        if (mimeType.includes('javascript') || mimeType.includes('json') ||
            mimeType.includes('xml') || mimeType.includes('html') ||
            mimeType.includes('css') || mimeType.includes('text/plain')) return mdiFileCode

        // Archives
        if (mimeType.includes('zip') || mimeType.includes('tar') ||
            mimeType.includes('gzip') || mimeType.includes('rar')) return mdiArchive

        // Default
        return SECTION_ICONS.documents
    }

    const thumbnailUrl = useMemo(() => {
        return `${apiBaseUrl}/api/v1/organizations/${org}/documents/${uid}/thumb?size=64`
    }, [uid, org])

    // Show thumbnail if available for images/PDFs
    if (isImageOrPdf(file_mime_type) && thumbnailUrl && !thumbFailed) {
        return (
            <img
                src={thumbnailUrl}
                alt={`${name} thumbnail`}
                className="w-12 h-12 object-cover rounded border shadow-sm"
                onError={() => setThumbFailed(true)}
            />
        )
    }

    // Show icon for other file types or when thumbnail fails
    return (
        <AppIcon tone="muted" path={getFileTypeIcon(file_mime_type)} size="xl" />
    )
}

interface AssetDocumentsProps {
    org: string
    assetUid: string
}

/** Maximum page size accepted by GET /documents (see documents handler). */
const ASSET_DOCUMENTS_PAGE_SIZE = 500

export function AssetDocuments({ org, assetUid }: AssetDocumentsProps) {
    const { t } = useTranslation('assets')
    const { t: tFolders, i18n } = useTranslation('folders')
    // const queryClient = useQueryClient()
    // 500 is the API's maximum page size: without an explicit limit the list
    // defaults to 20, which silently truncated the folder grouping below (and
    // its per-folder counts) for any asset with more documents than that.
    const { data: allDocuments = [] } = useDocuments(org, assetUid, undefined, undefined, ASSET_DOCUMENTS_PAGE_SIZE)
    // Folder hierarchy resolved for this asset through its site ancestor
    // (spec 2026-07-27-06). Empty `folders` → keep the flat list.
    const { data: folderTree } = useAssetDocumentFolders(org, assetUid)
    const folders = useMemo(() => folderTree?.folders ?? [], [folderTree])
    // Optional deposit session for this page-session's uploads (spec
    // 2026-08-16-05 §2) — plain state, never persisted.
    const [selectedImportUid, setSelectedImportUid] = useState<string>('')
    const uploadMutation = useUploadDocument(org, selectedImportUid || undefined)

    // Write side of the folder layout (spec 2026-07-30-01). The picker only
    // ever opens on a site-level asset (`editable`), and on such an asset
    // `site_asset_uid` *is* this asset — so the dialog always targets
    // `assetUid` and the asset query gives it the layout currently set on this
    // asset's own column plus its descendant count.
    const [layoutDialogOpen, setLayoutDialogOpen] = useState(false)
    const { data: asset } = useAsset(org, assetUid)
    const layoutEditable = folderTree?.editable === true
    const siteAssetUid = folderTree?.site_asset_uid
    const siteAssetName = folderTree?.site_asset_name
    const layoutName = folderTree?.layout
        ? getLocalizedName(folderTree.layout.name as Record<string, string> | undefined, i18n.language) || folderTree.layout.code
        : null
    // UID of the *resolved* layout (already embedded in the folders payload),
    // used to link a typed-but-unclassified document straight to its editor.
    const layoutUid = folderTree?.layout?.uid ?? null

    // Document whose "move to folder…" dialog is open (spec 2026-08-01-20 §2).
    const [moveDocument, setMoveDocument] = useState<DocumentWithType | null>(null)

    // Live entity notifications: this view is a filtered slice of the org's
    // documents collection (asset_uid is a query param, not a distinct
    // scope), so the coarse `documents` collection subscription is correct
    // here — a change to any document in the org refetches this list, which
    // is cheap since it's already asset-scoped server-side.
    useLiveSubscription({ entity: 'documents' })

    // Drag and drop states
    const [dragOver, setDragOver] = useState(false)
    const dragLeaveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
    const fileInputRef = useRef<HTMLInputElement>(null)
    const [showUploadInterface, setShowUploadInterface] = useState(false)
    const [uploadFiles, setUploadFiles] = useState<UploadFile[]>([])
    const [isProcessing, setIsProcessing] = useState(false)

    // Cleanup drag timeout on unmount and prevent global drag behavior
    useEffect(() => {
        // Prevent default drag behavior globally to avoid browser file navigation
        const handleGlobalDragOver = (e: DragEvent) => {
            e.preventDefault()
        }

        const handleGlobalDrop = (e: DragEvent) => {
            e.preventDefault()
        }

        // Add global event listeners to prevent browser default behavior
        document.addEventListener('dragover', handleGlobalDragOver)
        document.addEventListener('drop', handleGlobalDrop)

        return () => {
            if (dragLeaveTimeoutRef.current) {
                // eslint-disable-next-line react-hooks/exhaustive-deps
                clearTimeout(dragLeaveTimeoutRef.current)
            }
            // Remove global event listeners
            document.removeEventListener('dragover', handleGlobalDragOver)
            document.removeEventListener('drop', handleGlobalDrop)
        }
    }, [])

    // Newest first. In folder mode every document is shown (grouped); in flat
    // mode the view keeps its historical "last 10 uploads" digest.
    const sortedDocuments = useMemo(
        () => [...allDocuments].sort(
            (a, b) => new Date(b.created_at || '').getTime() - new Date(a.created_at || '').getTime()
        ),
        [allDocuments]
    )
    const last10Documents = sortedDocuments.slice(0, 10)

    // Computed values for upload status
    const allUploadsComplete = uploadFiles.every(f => f.status === 'success' || f.status === 'failed')
    const allUploadsSuccessful = uploadFiles.length > 0 && uploadFiles.every(f => f.status === 'success')

    // Upload file function
    const uploadSingleFile = useCallback(async (uploadFile: UploadFile) => {
        const { file, id } = uploadFile

        // Create abort controller for cancellation
        const abortController = new AbortController()

        // Update upload file with abort controller and set status to uploading
        setUploadFiles(prev => prev.map(f =>
            f.id === id ? { ...f, status: 'uploading', progress: 0, abortController } : f
        ))

        try {
            const formData = new FormData()
            formData.append('file', file)
            appendOriginalPath(formData, uploadFile.originalPath)

            // Always associate with the current asset. Document type is left
            // unset so the backend can infer it from the filename pattern
            // (e.g. "[BUILDING_RULES] something.pdf") or fall back to the
            // async classifier — same behavior as the standalone uploader.
            formData.append('asset_uid', assetUid)

            await uploadMutation.mutateAsync(formData)

            setUploadFiles(prev => prev.map(f =>
                f.id === id ? { ...f, status: 'success', progress: 100, abortController: undefined } : f
            ))
        } catch (err) {
            const errorMessage = err instanceof Error ? err.message : 'Failed to upload document'

            setUploadFiles(prev => {
                const fileToUpdate = prev.find(f => f.id === id)
                if (!fileToUpdate) return prev

                // If upload was cancelled, don't retry
                if (errorMessage.includes('cancelled') || errorMessage.includes('abort')) {
                    return prev.map(f =>
                        f.id === id
                            ? { ...f, status: 'failed', error: errorMessage, abortController: undefined }
                            : f
                    )
                }

                if (fileToUpdate.retryCount < 3) {
                    // Retry
                    return prev.map(f =>
                        f.id === id
                            ? { ...f, status: 'pending', retryCount: f.retryCount + 1, error: errorMessage, abortController: undefined }
                            : f
                    )
                } else {
                    // Max retries reached
                    return prev.map(f =>
                        f.id === id
                            ? { ...f, status: 'failed', error: errorMessage, abortController: undefined }
                            : f
                    )
                }
            })
        }
    }, [uploadMutation, assetUid])

    // Process upload queue
    useEffect(() => {
        const processQueue = async () => {
            if (isProcessing || uploadFiles.length === 0) return

            const pendingFiles = uploadFiles.filter(f => f.status === 'pending')
            if (pendingFiles.length === 0) {
                setIsProcessing(false)
                return
            }

            setIsProcessing(true)
            const filesToProcess = pendingFiles.slice(0, 2) // Process 2 files concurrently

            // Upload files concurrently using Promise.all
            await Promise.all(filesToProcess.map(uploadFile => uploadSingleFile(uploadFile)))

            setIsProcessing(false)
        }

        processQueue()
    }, [uploadFiles, isProcessing, uploadSingleFile])

    // Auto-close upload interface when all uploads are successful
    useEffect(() => {
        if (allUploadsSuccessful && showUploadInterface) {
            const timer = setTimeout(() => {
                setShowUploadInterface(false)
                setUploadFiles([])
            }, 2000) // Show success for 2 seconds

            return () => clearTimeout(timer)
        }
    }, [allUploadsSuccessful, showUploadInterface])

    // Helper functions for upload UI
    const formatFileSize = (bytes: number | undefined) => {
        if (!bytes) return t('asset_documents.unknown_size')
        const units = t('asset_documents.size_units', { returnObjects: true }) as string[]
        let size = bytes
        let unitIndex = 0
        while (size >= 1024 && unitIndex < units.length - 1) {
            size /= 1024
            unitIndex++
        }
        return `${size.toFixed(1)} ${units[unitIndex]}`
    }

    const getStatusIcon = (status: UploadFile['status']) => {
        switch (status) {
            case 'success':
                return <AppIcon path={mdiCheckCircle} size="lg" />
            case 'failed':
                return <AppIcon path={mdiAlertCircle} size="lg" className="text-destructive" />
            case 'uploading':
                return <AppIcon path={mdiProgressClock} size="lg" className="text-primary motion-safe:animate-spin" />
            case 'pending':
                return <AppIcon tone="muted" path={mdiProgressClock} size="lg" />
        }
    }

    const getStatusColor = (status: UploadFile['status']) => {
        switch (status) {
            case 'success':
                return 'text-foreground bg-muted/50'
            case 'failed':
                return 'text-destructive bg-destructive/10'
            case 'uploading':
                return 'text-primary bg-primary/10'
            case 'pending':
                return 'text-muted-foreground bg-muted/50'
        }
    }

    // Every document type claimed by a folder of the resolved layout. A
    // document whose type is outside this set (or that has no type) is what the
    // tree renders in its trailing "unclassified" bucket — see
    // `groupDocumentsByFolder`, which keys membership on the type alone.
    const classifiedTypeUids = useMemo(() => {
        const set = new Set<string>()
        for (const folder of folders) {
            for (const uid of folder.document_type_uids ?? []) set.add(uid)
        }
        return set
    }, [folders])

    // One document row, shared by the flat list and the folder tree (the tree
    // only supplies the indentation depth).
    const renderDocumentRow = (doc: DocumentWithType, depth: number) => {
        const typeUid = doc.document_type_uid
        // Typed but in no folder → the layout is missing a folder for that
        // type; offer the editor pre-filled with it (spec 2026-08-01-20 §3).
        const proposable = Boolean(typeUid) && !classifiedTypeUids.has(typeUid as string) && Boolean(layoutUid)
        return (
        <div
            key={doc.uid}
            className="flex items-center justify-between py-2 px-3 rounded hover:bg-muted/50"
            style={depth > 0 ? { marginLeft: depth * 16 } : undefined}
            data-testid={`asset-document-row-${doc.uid}`}
        >
            <div className="flex items-center gap-3">
                <Link
                    to="/$org/documents/$docUid"
                    params={{ org, docUid: doc.uid }}
                    data-testid={`asset-document-preview-${doc.uid}`}
                >
                    <DocumentIcon document={doc} org={org} />
                </Link>
                <div>
                    <Link
                        to="/$org/documents/$docUid"
                        params={{ org, docUid: doc.uid }}
                        className="text-primary hover:text-primary/80 text-sm font-medium"
                        data-testid={`asset-document-link-${doc.uid}`}
                    >
                        {doc.name}
                    </Link>
                    {doc.created_at && (
                        <p className="text-xs text-muted-foreground">
                            {new Date(doc.created_at).toLocaleDateString()}
                        </p>
                    )}
                </div>
            </div>
            <div className="flex items-center gap-2 text-right">
                <p className="text-xs text-muted-foreground">
                    {formatFileSize(doc.file_size)}
                </p>
                {proposable && layoutUid && (
                    <Button
                        asChild
                        variant="outline"
                        size="icon"
                        className="h-7 w-7 shrink-0"
                        aria-label={tFolders('asset_view.propose_folder')}
                        title={tFolders('asset_view.propose_folder')}
                        data-testid={`asset-document-propose-folder-${doc.uid}`}
                    >
                        <Link
                            to="/$org/folder-layouts/$layoutUid"
                            params={{ org, layoutUid }}
                            search={{ document_type_uid: typeUid as string }}
                        >
                            <AppIcon path={mdiFolderPlusOutline} size="sm" />
                        </Link>
                    </Button>
                )}
                {folders.length > 0 && (
                    <Button
                        variant="outline"
                        size="icon"
                        className="h-7 w-7 shrink-0"
                        onClick={() => setMoveDocument(doc)}
                        aria-label={tFolders('asset_view.move_document')}
                        title={tFolders('asset_view.move_document')}
                        data-testid={`asset-document-move-${doc.uid}`}
                    >
                        <AppIcon path={mdiFolderMove} size="sm" />
                    </Button>
                )}
            </div>
        </div>
        )
    }

    // Simple drag and drop handlers - only activate on container level
    const handleDocumentsAreaDragEnter = (e: React.DragEvent) => {
        e.preventDefault()
        if (e.dataTransfer.items && e.dataTransfer.items.length > 0) {
            setDragOver(true)
        }
    }

    const handleDropZoneDragOver = (e: React.DragEvent) => {
        e.preventDefault()
        e.stopPropagation()
    }

    const handleDropZoneDragLeave = (e: React.DragEvent) => {
        e.preventDefault()
        e.stopPropagation()
        // Check if we're actually leaving the drop zone boundaries
        const rect = (e.currentTarget as HTMLElement).getBoundingClientRect()
        if (e.clientX < rect.left || e.clientX > rect.right ||
            e.clientY < rect.top || e.clientY > rect.bottom) {
            setDragOver(false)
        }
    }

    const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const files = filesFromFileList(e.target.files)
        if (files.length > 0) {
            const newUploadFiles: UploadFile[] = files.map(({ file, originalPath }) => ({
                file,
                id: `${file.name}-${Date.now()}-${Math.random()}`,
                status: 'pending',
                progress: 0,
                retryCount: 0,
                originalPath,
            }))

            setUploadFiles(prev => [...prev, ...newUploadFiles])
            setShowUploadInterface(true)
        }
        // Reset input so the same file can be selected again
        if (fileInputRef.current) {
            fileInputRef.current.value = ''
        }
    }

    const handleDropZoneDrop = async (e: React.DragEvent) => {
        e.preventDefault()
        e.stopPropagation()
        setDragOver(false)

        // Dropped folders are expanded, each file keeping its relative path
        // (spec 2026-08-16-04 §3).
        const files = await collectDroppedFiles(e.dataTransfer)
        if (files.length > 0) {
            const newUploadFiles: UploadFile[] = files.map(({ file, originalPath }) => ({
                file,
                id: `${file.name}-${Date.now()}-${Math.random()}`,
                status: 'pending',
                progress: 0,
                retryCount: 0,
                originalPath,
            }))

            setUploadFiles(prev => [...prev, ...newUploadFiles])
            setShowUploadInterface(true)
        }
    }

    return (
        <>
            {/* Upload Interface Modal */}
            {showUploadInterface && (
                <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center">
                    <div className="bg-card rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[80vh] overflow-hidden border">
                        <div className="p-6 border-b border-border">
                            <div className="flex justify-between items-center">
                                <h2 className="text-xl font-semibold text-foreground">{t('asset_documents.upload_progress_title')}</h2>
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => {
                                        if (allUploadsComplete) {
                                            setShowUploadInterface(false)
                                            setUploadFiles([])
                                        }
                                    }}
                                    disabled={!allUploadsComplete}
                                    data-testid="close-upload-modal"
                                >
                                    <AppIcon path={mdiClose} size="md" />
                                </Button>
                            </div>
                        </div>

                        <div className="p-6 overflow-y-auto max-h-[60vh]">
                            {uploadFiles.length === 0 ? (
                                <EmptyState variant="inline" title={t('asset_documents.no_files')} />
                            ) : (
                                <div className="space-y-3">
                                    {uploadFiles.map((uploadFile) => (
                                        <div
                                            key={uploadFile.id}
                                            className={`p-4 rounded-lg border ${getStatusColor(uploadFile.status)}`}
                                        >
                                            <div className="flex items-center justify-between">
                                                <div className="flex items-center space-x-3">
                                                    {getStatusIcon(uploadFile.status)}
                                                    <div>
                                                        <p className="font-medium text-sm">{uploadFile.file.name}</p>
                                                        <p className="text-xs opacity-75">
                                                            {formatFileSize(uploadFile.file.size)}
                                                        </p>
                                                    </div>
                                                </div>
                                                <div className="text-right">
                                                    <div className="flex items-center gap-2">
                                                        <div>
                                                            <p className="text-sm font-medium capitalize">{uploadFile.status}</p>
                                                            {uploadFile.status === 'uploading' && (
                                                                <p className="text-xs">{uploadFile.progress}%</p>
                                                            )}
                                                            {uploadFile.retryCount > 0 && (
                                                                <p className="text-xs">{t('asset_documents.retry', { count: uploadFile.retryCount })}</p>
                                                            )}
                                                        </div>
                                                        {uploadFile.status === 'uploading' && uploadFile.abortController && (
                                                            <Button
                                                                variant="ghost"
                                                                size="sm"
                                                                onClick={() => {
                                                                    uploadFile.abortController?.abort()
                                                                }}
                                                                className="h-6 w-6 p-0"
                                                                data-testid="cancel-upload"
                                                            >
                                                                <AppIcon path={mdiClose} size="xs" />
                                                            </Button>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                            {uploadFile.status === 'uploading' && (
                                                <div className="mt-3">
                                                    <div className="bg-muted rounded-full h-2">
                                                        <div
                                                            className="bg-primary h-2 rounded-full transition-all duration-300"
                                                            style={{ width: `${uploadFile.progress}%` }}
                                                        />
                                                    </div>
                                                </div>
                                            )}
                                            {uploadFile.error && (
                                                <p className="text-xs mt-2 text-destructive">{uploadFile.error}</p>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>

                        <div className="p-6 border-t border-border">
                            <div className="flex justify-between items-center">
                                <div>
                                    <p className="text-sm text-muted-foreground">
                                        {t('asset_documents.status_successful', { count: uploadFiles.filter(f => f.status === 'success').length })}
                                        {uploadFiles.filter(f => f.status === 'failed').length > 0 &&
                                            `, ${t('asset_documents.status_failed', { count: uploadFiles.filter(f => f.status === 'failed').length })}`
                                        }
                                        {uploadFiles.filter(f => f.status === 'pending' || f.status === 'uploading').length > 0 &&
                                            `, ${t('asset_documents.status_pending', { count: uploadFiles.filter(f => f.status === 'pending' || f.status === 'uploading').length })}`
                                        }
                                    </p>
                                </div>
                                {allUploadsComplete && (
                                    <Button onClick={() => {
                                        setShowUploadInterface(false)
                                        setUploadFiles([])
                                    }}>
                                        {t('asset_documents.close')}
                                    </Button>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Action button — free-standing, above the content box.
                The optional deposit session (spec 2026-08-16-05 §2) sits next
                to it: this surface uploads as soon as files are picked, so the
                selector must be reachable *before* the picker opens. */}
            <div className="flex flex-wrap justify-end items-center gap-2">
                <ImportSelect
                    org={org}
                    value={selectedImportUid}
                    onChange={setSelectedImportUid}
                    className="w-full sm:w-64"
                    testidPrefix="asset-documents-import-select"
                />
                <Can perm="documents.write">
                    <Button
                        size="sm"
                        onClick={() => fileInputRef.current?.click()}
                        aria-label={t('asset_documents.upload')}
                        title={t('asset_documents.upload')}
                        data-testid="upload-document-btn"
                    >
                        <AppIcon path={mdiUpload} size="sm" className="md:mr-1" />
                        <span className="hidden md:inline">{t('asset_documents.upload')}</span>
                    </Button>
                </Can>
            </div>

            {/* Last Documents */}
            <div
                className="bg-card rounded-lg border border-border p-6 relative"
                onDragEnter={handleDocumentsAreaDragEnter}
            >
                {/* Drop zone overlay - only visible when dragging */}
                {dragOver && (
                    <div
                        className="absolute inset-0 bg-primary/10 border-2 border-dashed border-primary rounded-lg z-10 flex items-center justify-center"
                        onDragOver={handleDropZoneDragOver}
                        onDragLeave={handleDropZoneDragLeave}
                        onDrop={handleDropZoneDrop}
                    >
                        <div className="text-center p-4">
                            <div className="text-lg font-semibold text-primary">{t('asset_documents.drop_title')}</div>
                            <div className="text-sm text-muted-foreground">{t('asset_documents.drop_hint')}</div>
                        </div>
                    </div>
                )}
                <input
                    ref={fileInputRef}
                    type="file"
                    multiple
                    className="hidden"
                    onChange={handleFileInputChange}
                    data-testid="upload-document-input"
                />
                {/*
                  * Folder-structure header row (spec 2026-07-30-01). Rendered
                  * above the list in *all* states — tree, flat list and empty —
                  * so a site with no layout yet can still reach the picker.
                  */}
                {folderTree && (
                    <div className="flex items-start justify-between gap-2 mb-3" data-testid="asset-documents-layout-row">
                        <p className="text-xs text-muted-foreground" data-testid="asset-documents-layout-name">
                            {layoutName
                                ? tFolders('asset_view.layout_label', { name: layoutName })
                                : tFolders('asset_view.layout_none')}
                            {/*
                              * Both type-default sources say "this comes from a
                              * type, not from a choice made on this asset" —
                              * `ancestor_asset_type` (spec 2026-08-01-24) just
                              * names the site whose type carries it.
                              */}
                            {folderTree.layout_source === 'asset_type' && (
                                <span> {tFolders('asset_view.layout_from_type')}</span>
                            )}
                            {folderTree.layout_source === 'ancestor_asset_type' && (
                                <span> {tFolders('asset_view.layout_from_ancestor_type')}</span>
                            )}
                            {!layoutEditable && siteAssetUid && (
                                <span>
                                    {' '}
                                    {/*
                                      * "inherited from" only fits the ancestor
                                      * case. With no layout at all, or one
                                      * coming from the asset type, nothing is
                                      * inherited from the site — the link is
                                      * then an invitation to go set one there.
                                      */}
                                    {folderTree.layout_source === 'ancestor'
                                        ? tFolders('asset_view.layout_label_inherited')
                                        : tFolders('asset_view.layout_none_managed_on')}{' '}
                                    <Link
                                        to="/$org/assets/$assetUid"
                                        params={{ org, assetUid: siteAssetUid }}
                                        search={{ tab: 'documents' } as never}
                                        className="underline underline-offset-2 hover:text-foreground"
                                        data-testid="asset-documents-layout-site-link"
                                    >
                                        {siteAssetName || siteAssetUid}
                                    </Link>
                                </span>
                            )}
                        </p>
                        {layoutEditable && (
                            <Button
                                variant="outline"
                                size="icon"
                                className="shrink-0"
                                onClick={() => setLayoutDialogOpen(true)}
                                aria-label={tFolders('asset_view.settings_button')}
                                title={tFolders('asset_view.settings_button')}
                                data-testid="asset-documents-layout-settings-btn"
                            >
                                <AppIcon path={mdiFolderCog} size="md" />
                            </Button>
                        )}
                    </div>
                )}
                {layoutEditable && (
                    <AssetFolderLayoutDialog
                        open={layoutDialogOpen}
                        onOpenChange={setLayoutDialogOpen}
                        org={org}
                        assetUid={assetUid}
                        siteAssetUuid={siteAssetUid ?? undefined}
                        currentLayoutUid={asset?.document_folder_layout_uid as string | undefined}
                    />
                )}
                {/* Mounted per selected document so the update mutation is
                    bound to that document's uid. */}
                {moveDocument && (
                    <DocumentMoveFolderDialog
                        open
                        onOpenChange={open => { if (!open) setMoveDocument(null) }}
                        org={org}
                        assetUid={assetUid}
                        folders={folders}
                        document={moveDocument}
                    />
                )}
                {folders.length > 0 ? (
                    <div data-testid="asset-documents-folder-tree">
                        <DocumentFolderTree
                            folders={folders}
                            documents={sortedDocuments as DocumentWithType[]}
                            renderDocument={renderDocumentRow}
                            countMode="rolledUp"
                        />
                    </div>
                ) : last10Documents.length > 0 ? (
                    <div className="space-y-2">
                        {last10Documents.map((doc) => renderDocumentRow(doc as DocumentWithType, 0))}
                    </div>
                ) : (
                    <EmptyState
                        variant="inline"
                        className="py-12"
                        icon={SECTION_ICONS.documents}
                        title={t('asset_documents.empty')}
                        description={t('asset_documents.empty_hint')}
                        data-testid="asset-documents-empty"
                    />
                )}
            </div>
        </>
    )
}